import { useEffect, useSyncExternalStore, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { TopBarPortal } from '@clarix/layout';
import { NextMeetingChip } from './NextMeetingChip';
import { QuickMeetingMic } from './QuickMeetingMic';
import { useQuickMeeting } from './QuickMeetingContext';

// The TopBar's bell slot target (packages/layout TopBarAppSlots). Portalled WITHOUT registering,
// so the shell's own bell still shows on pages where no app fills that slot.
const BELL_SLOT_DOM_ID = 'clarix-topbar-slot-notifications';

// The TopBar commits in the same pass as this component, so look again on the next frame.
const nextFrame = (onChange: () => void) => {
  const id = requestAnimationFrame(onChange);
  return () => cancelAnimationFrame(id);
};
const bellSlot = () => document.getElementById(BELL_SLOT_DOM_ID);

function BeforeBellPortal({ children }: { children: ReactNode }) {
  const target = useSyncExternalStore(nextFrame, bellSlot, () => null);
  return target ? createPortal(<div className="order-first flex items-center">{children}</div>, target) : null;
}

/**
 * Apps fill this slot too (Projects' meetings; Support's "Active" pill pulls itself left with a
 * negative margin). Putting the chip's box first in the DOM lets it cancel that margin, but only
 * while the chip shows, so a hidden or empty chip leaves the next item as it was.
 */
const leadTheSlot = (el: HTMLDivElement | null) => {
  const slot = el?.parentElement;
  if (el && slot && slot.firstElementChild !== el) slot.prepend(el);
};

/**
 * The chip at the far left of the TopBar (`meeting-chip` slot, ahead of any app's own chip) and
 * the mic just before the app's bell (no `packages/layout` edit). Portals find their target only
 * on mount, so AppShellLayout keys this on `hideChrome`: the TopBar remounts only then.
 */
export function QuickMeetingTopBar({ visible }: { visible: boolean }) {
  const attach = useQuickMeeting()?.attachTopBar;

  useEffect(() => {
    if (!visible || !attach) return;
    return attach();
  }, [visible, attach]);

  if (!visible || !attach) return null;
  return (
    <>
      <TopBarPortal slot="meeting-chip">
        <div
          ref={leadTheSlot}
          className="order-first hidden items-center empty:hidden lg:flex lg:[&:not(:empty)+*]:ml-0"
        >
          <NextMeetingChip />
        </div>
      </TopBarPortal>
      <BeforeBellPortal>
        <QuickMeetingMic />
      </BeforeBellPortal>
    </>
  );
}
